import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { PlusIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import { addCurriculum } from '@/features/curriculums/curriculums-api';
import {
  type Locale,
  supportedLanguages,
} from '@/features/locale/supported-languages';
import { curriculumNamesSchema } from '../actions.schema';

function emptyNames(): Record<Locale, string> {
  return Object.fromEntries(
    supportedLanguages.map((locale) => [locale, '']),
  ) as Record<Locale, string>;
}

function AddCurriculumForm({
  locationId,
  onSuccess,
}: {
  locationId: number;
  onSuccess: () => void;
}) {
  const t = useTranslations('admin');
  const t_lang = useTranslations('languages');
  const [names, setNames] = useState<Record<Locale, string>>(emptyNames);
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);

    const submission = curriculumNamesSchema.safeParse(names);
    if (!submission.success) {
      setError(t('name_in', { locale: t_lang('en') }));
      return;
    }

    setPending(true);
    try {
      const result = await addCurriculum(submission.data, locationId);
      if (result.ok) {
        onSuccess();
      } else {
        setError(result.error);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="font-semibold">{t('name')}</div>
      {supportedLanguages.map((locale) => (
        <div key={locale} className="flex flex-col gap-2">
          <Label htmlFor={`add-curriculum-${locale}`}>
            {t('name_in', { locale: t_lang(locale) })}
          </Label>
          <Input
            id={`add-curriculum-${locale}`}
            type="text"
            value={names[locale]}
            onChange={(e) =>
              setNames((prev) => ({ ...prev, [locale]: e.target.value }))
            }
          />
        </div>
      ))}
      {error && <div className="text-destructive text-sm">{error}</div>}
      <SubmitButton
        label={t('add', { what: t('curriculum').toLowerCase() })}
        pending={pending}
      />
    </form>
  );
}

export default function AddCurriculumDialog({
  locationId,
  onCreated,
}: {
  locationId: number;
  onCreated: () => void;
}) {
  const t = useTranslations('admin');
  const [open, setOpen] = useState(false);
  const title = t('add', { what: t('curriculum').toLowerCase() });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-5 w-5" />
          {title}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <AddCurriculumForm
          locationId={locationId}
          onSuccess={() => {
            setOpen(false);
            onCreated();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
